/**
 * Pre-login tenant resolution for the PWA (mirrors the shell's
 * tenant-resolver): the tenant is resolved by HOST from tenant-service's
 * public branding projection, never from a client-supplied id. The resolved
 * tenantId is what `api-client` sends as `X-Tenant-ID` (via the auth store's
 * `tenantConfig`) and what the auth screens pass to `sessionClient` — the
 * backend's `TenantResolutionFilter` fails closed without it
 * (MISSING_TENANT_400_001), including on the public /auth/* routes.
 */
import { fetchPublicBranding } from '@quickbee/api-tenant';
import { useAuthStore, type TenantConfig } from '@quickbee/auth';
import type { SessionClientOptions } from '@quickbee/auth/internal';
import { applyTenantBranding } from '@/theme/tenantBrand';

/** Emitted at most once per page load — a warning per render would be noise. */
let warnedFallback = false;

export async function initTenant(): Promise<void> {
  const branding = await fetchPublicBranding(window.location.hostname);
  if (!branding) {
    // The last brand this host resolved, if there is one. `fetchPublicBranding`
    // flattens every failure to null — a genuine 404 and a dropped connection are
    // indistinguishable here — and the cached answer is the better bet on both
    // counts: it keeps a jeweller's app looking like theirs, and it keeps a tenant
    // id, without which TenantResolutionFilter refuses every authenticated call and
    // the session cannot even be refreshed.
    const cached = readCachedTenant();
    if (!warnedFallback) {
      warnedFallback = true;
      console.warn(
        `[tenant] Public branding for host "${window.location.hostname}" could not be ` +
          (cached
            ? `loaded; continuing on the brand last cached for this host ("${cached.config.tenantId}"). `
            : 'loaded — no tenant id is available, so authenticated calls will be refused ' +
              'by TenantResolutionFilter. ') +
          'If this is a deployed environment, ' +
          'GET /tenants/public/by-host/{host} is missing or unrouted ' +
          '(backend-api-integration-design.md §16 B6).'
      );
    }
    if (cached) useAuthStore.getState().setTenantConfig(cached.config);
    return;
  }

  const config: TenantConfig = {
    tenantId: branding.tenantId,
    name: branding.name,
    logoUrl: branding.logoUrl ?? '',
    primaryColor: branding.primaryColor,
    secondaryColor: branding.secondaryColor,
    fontFamily: branding.fontFamily ?? 'default',
    appName: branding.appName,
    themeMode: branding.themeMode ?? 'system',
    // Not part of the public projection — populated post-login if ever needed.
    schemes: [],
    featureFlags: branding.publicFeatureFlags ?? {},
    allowedRoles: ['SAVER'],
    supportEmail: branding.supportEmail ?? '',
    tnc_url: branding.tncUrl ?? ''
  };
  useAuthStore.getState().setTenantConfig(config);

  // The white-label half of the resolution: the host decided WHO the tenant
  // is, this makes the app LOOK like them. Logo and app name are read from
  // tenantConfig by the components that render them; the colour has to be
  // pushed into the token cascade here, before first paint.
  const css = applyTenantBranding(config.primaryColor, branding.secondaryColor);
  const favicon = applyTenantFavicon(config);
  applyTenantMeta(config);
  cacheTenant(config, css, favicon);
}

/* ------------------------------------------------------------------ */
/* Pre-paint brand cache                                               */
/* ------------------------------------------------------------------ */

/**
 * What `initTenant` remembers about a host, and what the blocking brand script in
 * `index.html` replays before first paint on the next visit.
 *
 * The problem it solves: this app ships one bundle to every tenant's domain, with
 * the product's own palette in the stylesheet and QuickBee's name in `<title>`, and
 * the tenant is not known until `GET /tenants/public/by-host` answers. React mounts
 * without waiting for it (`main.tsx` does not await `initTenant` before rendering),
 * so a jeweller's saver watched the platform's brand for a round trip and then saw
 * it swap to their own. There is no server to ask sooner; the only earlier answer
 * available is the one from last time.
 *
 * Keyed by hostname, because the tenant is — one phone may hold several jewellers'
 * brands, and each must only ever be replayed onto its own host.
 */
interface CachedTenant {
  v: number;
  css: string | null;
  favicon: string | null;
  appName: string;
  themeColor: string;
  config: TenantConfig;
}

/** Bumped when the shape changes, so a stale entry is ignored rather than replayed. */
const BRAND_CACHE_VERSION = 1;

function brandCacheKey(): string {
  return `qb-brand:${window.location.hostname}`;
}

function cacheTenant(config: TenantConfig, css: string | null, favicon: string | null): void {
  try {
    const entry: CachedTenant = {
      v: BRAND_CACHE_VERSION,
      css,
      favicon,
      appName: config.appName ?? '',
      themeColor: config.primaryColor ?? '',
      // The whole config, not only what the boot script paints. The stylesheet fixes
      // the COLOURS before first paint; the logo and the tenant's name are React's to
      // draw, and React reads the store — which stays empty until the fetch answers.
      config,
    };
    localStorage.setItem(brandCacheKey(), JSON.stringify(entry));
  } catch {
    // Storage denied — the visitor pays a one-frame flash. Never fatal.
  }
}

function readCachedTenant(): CachedTenant | null {
  try {
    const raw = localStorage.getItem(brandCacheKey());
    if (!raw) return null;
    const cached = JSON.parse(raw) as Partial<CachedTenant>;
    if (cached.v !== BRAND_CACHE_VERSION || !cached.config?.tenantId) return null;
    return cached as CachedTenant;
  } catch {
    return null;
  }
}

/**
 * Seeds the store from this host's cached brand, synchronously, before React mounts.
 *
 * The companion to the boot script: that one paints, this one gives the components
 * the logo and the name they draw. Everything derived from it is provisional until
 * `initTenant` answers — including the tenant id, which is the same id the previous
 * visit authenticated against, and which `initTenant` overwrites either way.
 */
export function hydrateTenantFromCache(): void {
  const cached = readCachedTenant();
  if (cached) useAuthStore.getState().setTenantConfig(cached.config);
}

/**
 * Tab title and the colour the browser paints its own chrome with. Both ship
 * hard-coded in `index.html`, which is one file for every tenant's domain.
 */
function applyTenantMeta(config: TenantConfig): void {
  if (config.appName) document.title = config.appName;
  if (!config.primaryColor) return;
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = config.primaryColor;
}

const FAVICON_ID = 'qb-tenant-favicon';
const DEFAULT_PRIMARY_HEX = '#8b1a3c';

/**
 * Swaps the browser-tab icon to a generated tenant mark (rounded square in the
 * tenant primary, white initial, gold spark). Derived from the palette rather
 * than `logoUrl` — a wordmark cannot shrink to 16px. The default brand keeps
 * the shipped QuickBee icons untouched.
 */
function applyTenantFavicon(config: TenantConfig): string | null {
  const hex = (config.primaryColor ?? '').trim().toLowerCase();
  if (!hex || hex === DEFAULT_PRIMARY_HEX || document.getElementById(FAVICON_ID)) return null;

  // Neutralise the shipped icons (rel="" keeps the element, drops the meaning)
  // so the generated one is the only candidate the browser sees.
  document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]').forEach((link) => {
    link.rel = '';
  });

  const href = tenantMarkDataUri(config);
  const link = document.createElement('link');
  link.id = FAVICON_ID;
  link.rel = 'icon';
  link.type = 'image/svg+xml';
  link.href = href;
  document.head.appendChild(link);
  return href;
}

/**
 * The tenant's mark as an inline SVG data URI — a rounded square in their primary,
 * their initial, and the gold spark.
 *
 * Shared by the favicon and the splash screen. The splash used to show
 * `/images/quickbee-mark.png`, a bee, on every tenant's domain: a decorative image
 * is still a brand, and it was the platform's rather than the one whose app the
 * saver opened. Derived from the palette rather than `logoUrl` because both callers
 * need a square — a wordmark neither shrinks to 16px nor fills a splash medallion.
 */
export function tenantMarkDataUri(config: {
  primaryColor?: string | null;
  appName?: string | null;
  name?: string | null;
}): string {
  const hex = (config.primaryColor ?? '').trim().toLowerCase() || DEFAULT_PRIMARY_HEX;
  const initial = (config.appName || config.name || 'Q').trim().charAt(0).toUpperCase();
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64">` +
    `<rect width="64" height="64" rx="14" fill="${hex}"/>` +
    `<path d="M50 8 L54 14 L50 20 L46 14 Z" fill="#F5C542"/>` +
    `<text x="32" y="44" text-anchor="middle" font-family="Georgia,serif" font-size="34" font-weight="bold" fill="#fff">${initial}</text>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * Options for every `@quickbee/auth/internal` sessionClient call: carries the
 * resolved tenant id so `X-Tenant-ID` is sent on register / login / verify /
 * refresh / logout. Returns `{}` only when no tenant could be resolved (the
 * server will then answer MISSING_TENANT_400_001, surfaced inline).
 */
export function sessionOptions(): SessionClientOptions {
  const state = useAuthStore.getState();
  const tenantId = state.user?.tenantId ?? state.tenantConfig?.tenantId;
  return tenantId ? { tenantId } : {};
}
